import { useCallback, useEffect, useState } from "react";
import { Button, Form, Skeleton, Space, Spin, Tag } from "antd";
import { ArrowLeftOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiClient from "../../helpers/apiClient";
import useUrlParamState from "../../hooks/useUrlParamState";
import displayValue from "../../utilities/displayValue";
import uploadFile from "../../utilities/uploadFile";
import { useToast } from "../../contexts/ToastContext";
import { useUser } from "../../contexts/UserContext/UserContext";
import { getEnabledSaleProviders } from "../insurance/lib/insuranceSaleApi";
import TopBack from "../reservations/components/subcompdash/Topback";
import CarDetailFieldTable from "../cars/components/cardetails/CarDetailFieldTable";
import CarDetailTabs from "../cars/components/cardetails/CarDetailTabs";
import CustomerAvatar from "./components/customerdetail/CustomerAvatar";
import CustomerDetailEditForm, { customerToFormValues } from "./components/customerdetail/CustomerDetailEditForm";
import CustomerLicenseSection from "./components/customerdetail/CustomerLicenseSection";
import CustomerReservationsTab from "./components/customerdetail/CustomerReservationsTab";
import CustomerCommentsTab from "./components/customerdetail/CustomerCommentsTab";
import CustomerChangeLogTab from "./components/customerdetail/CustomerChangeLogTab";

function Customerdetails({ match }) {
    const { t } = useTranslation("customers");
    const { t: tTopBar } = useTranslation("topBar");
    const { t: tSell } = useTranslation("sellInsurance");
    const { myCompanyData } = useUser();
    const canSellInsurance = getEnabledSaleProviders(myCompanyData).length > 0;
    const history = useHistory();
    const toast = useToast();
    const [form] = Form.useForm();

    const [activeTabParam, setActive] = useUrlParamState("tab", 1, [1, 2, 3]);
    const active = Number(activeTabParam);
    const [err, setErr] = useState("");
    const [initialLoad, setInitialLoad] = useState(true);
    const [saving, setSaving] = useState(false);
    const [customerEdit, setCustomerEdit] = useState(false);
    const [customerinfo, setCustomerinfo] = useState(null);
    const [images, setImages] = useState([]);

    const [licenseVerificationStatus, setLicenseVerificationStatus] = useState(null);
    const [checkingVerification, setCheckingVerification] = useState(false);

    const customerId = match?.params?.id;
    const na = t("common.notAvailable");

    const fetchUserDetails = async (userEmail) => {
        try {
            const { data } = await apiClient.get(`/api/user/list?email=${userEmail}`);
            if (data?.success) {
                const user = data.data.result[0];
                return user?.drivingLicense?.driverApplicantId || null;
            }
            return null;
        } catch {
            return null;
        }
    };

    const checkVerificationStatus = useCallback(async (driverApplicantId) => {
        try {
            const [personalStatus, rideshareStatus] = await Promise.all([
                apiClient.get(`/api/driver/realtime-verification/PERSONAL/${driverApplicantId}`),
                apiClient.get(`/api/driver/realtime-verification/RIDESHARE/${driverApplicantId}`),
            ]);

            const personalPassed = personalStatus.data.data?.data?.status === "PASSED";
            const ridesharePassed = rideshareStatus.data.data?.data?.status === "PASSED";

            if (personalStatus.data.success && rideshareStatus.data.success) {
                return personalPassed || ridesharePassed ? "verified" : "unverified";
            }
            return "unverified";
        } catch {
            return "unverified";
        }
    }, []);

    const driverLicenseVerification = async (userEmail) => {
        try {
            const driverApplicantId = await fetchUserDetails(userEmail);
            if (!driverApplicantId) return "no_applicant_id";
            return checkVerificationStatus(driverApplicantId);
        } catch {
            return "unverified";
        }
    };

    const fetchData = useCallback(async () => {
        if (!customerId) return;

        setInitialLoad(true);
        try {
            const res = await apiClient.get(`/api/user/${customerId}`);
            if (res.data?.data) {
                setCustomerinfo(res.data.data);
                setImages(res.data.data.userImage ? [{ thumbUrl: res.data.data.userImage, url: res.data.data.userImage }] : []);
                form.setFieldsValue(customerToFormValues(res.data.data));
            }
        } catch {
            setErr(t("customerDetail.errorFetchingData"));
        } finally {
            setInitialLoad(false);
        }
    }, [customerId, form, t]);

    const handleLicenseVerification = async (email) => {
        if (!email) return;
        setCheckingVerification(true);
        try {
            const status = await driverLicenseVerification(email);
            setLicenseVerificationStatus(status);
        } catch {
            setLicenseVerificationStatus("error");
        } finally {
            setCheckingVerification(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (customerinfo?.email) {
            handleLicenseVerification(customerinfo.email);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerinfo?.email]);

    const handleFileChange = (file) => {
        if (file) setImages(file);
    };

    const handleCancelEdit = () => {
        form.setFieldsValue(customerToFormValues(customerinfo));
        setImages(customerinfo?.userImage ? [{ thumbUrl: customerinfo.userImage, url: customerinfo.userImage }] : []);
        setCustomerEdit(false);
    };

    const handleFormSubmit = async (values) => {
        const payload = {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email,
            address: values.address.trim(),
            mobileNumber: values.mobileNumber.trim(),
            dateOfBirth: values.dateOfBirth?.format?.("YYYY-MM-DD") || values.dateOfBirth,
            // "" clears it, so the customer's browser timezone is used again.
            timezone: values.timezone || "",
            name: `${values.firstName.trim()} ${values.lastName.trim()}`,
            license: {
                ...customerinfo?.drivingLicense,
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
            },
        };

        setSaving(true);
        try {
            if (images.length && images[0]?.originFileObj) {
                const imageData = await uploadFile(images[0].originFileObj);
                if (imageData) payload.userImage = imageData;
            }

            const resp = await apiClient.patch(`/api/user/${customerId}`, payload);
            if (resp?.data?.success) {
                toast.success({ message: t("customerDetail.updateSuccess") });
                setCustomerEdit(false);
                fetchData();
            }
        } catch {
            toast.error({ message: t("customerDetail.errorUpdatingCustomer") });
        } finally {
            setSaving(false);
        }
    };

    const detailRows = [
        { label: t("firstName"), value: customerinfo?.firstName },
        { label: t("lastName"), value: customerinfo?.lastName },
        { label: t("customerDetail.dateOfBirth"), value: customerinfo?.dateOfBirth },
        { label: t("customerDetail.contactNo"), value: customerinfo?.mobileNumber },
        { label: t("email"), value: customerinfo?.email },
        { label: t("address"), value: customerinfo?.address },
        {
            label: t("customerDetail.timezone"),
            value: customerinfo?.timezone || t("customerDetail.timezoneAuto"),
        },
    ];

    const CUSTOMER_DETAIL_TABS = [
        { id: 1, label: t("customerDetail.reservationHistory") },
        { id: 2, label: t("customerDetail.comments") },
        { id: 3, label: t("customerDetail.changeLog") },
    ];

    const renderActiveTab = () => {
        if (active === 1) {
            return (
                <CustomerReservationsTab
                    userId={customerId}
                    email={customerinfo?.email}
                    loading={initialLoad}
                />
            );
        }
        if (active === 2) {
            return <CustomerCommentsTab userId={customerId} />;
        }
        if (active === 3) {
            return <CustomerChangeLogTab />;
        }
        return null;
    };

    const customerName = customerinfo
        ? `${customerinfo.firstName || ""} ${customerinfo.lastName || ""}`.trim()
        : "";

    const statusTag = () => {
        if (!customerinfo?.status) return null;
        if (customerinfo.status.isApproved) {
            return (
                <Tag color="success" className="rounded border-0">
                    {t("verified")}
                </Tag>
            );
        }
        if (customerinfo.status.isReviewed) {
            return (
                <Tag color="warning" className="rounded border-0">
                    {t("pending")}
                </Tag>
            );
        }
        return null;
    };

    return (
        <div className="flex min-w-0 flex-col gap-3">
            <Spin spinning={saving} tip={t("customerDetail.updating")}>
                <div className="flex min-w-0 flex-col gap-3">
                    <div className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-4">
                        <div className="flex min-w-0 flex-wrap items-center gap-3">
                            <Button
                                type="text"
                                icon={<ArrowLeftOutlined className="text-base text-[#2F2F46]" />}
                                onClick={() => history.goBack()}
                                aria-label={tTopBar("goBack")}
                                className="!h-9 !w-9 shrink-0 !rounded-lg !p-0 hover:!bg-[#F2F2FA] sm:hidden"
                            />
                            <span className="hidden sm:inline-flex">
                                <TopBack />
                            </span>
                            <h1 className="fontbold text-[1.3rem]">{t("customerDetails")}</h1>
                        </div>
                        {err && <div className="text-red-600">{err}</div>}
                    </div>

                    {!customerId ? (
                        <div className="flex items-center justify-center py-12 text-[#666]">
                            {t("customerDetail.invalidCustomerId")}
                        </div>
                    ) : (
                        <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(360px,420px)_minmax(0,1fr)]">
                            <div className="flex min-w-0 flex-col rounded-2xl border border-[#f0f0f0] bg-white">
                                <div className="flex shrink-0 flex-wrap items-start justify-between gap-3 border-b border-[#f0f0f0] px-4 py-3">
                                    <div className="min-w-0 flex-1">
                                        {initialLoad ? (
                                            <Skeleton active paragraph={{ rows: 1 }} />
                                        ) : (
                                            <>
                                                <h2 className="fontbold flex flex-wrap items-center gap-2 text-lg text-[#121212]">
                                                    {displayValue(customerName, na)}
                                                    {statusTag()}
                                                </h2>
                                                <p className="mt-1 text-sm font-semibold text-gray-600">
                                                    {t("email")}: {displayValue(customerinfo?.email, na)}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex shrink-0 items-center">
                                        {customerEdit ? (
                                            <Space size={8}>
                                                <Button
                                                    type="primary"
                                                    shape="round"
                                                    loading={saving}
                                                    onClick={() => form.submit()}>
                                                    {t("customerDetail.save")}
                                                </Button>
                                                <Button
                                                    shape="round"
                                                    disabled={saving}
                                                    onClick={handleCancelEdit}>
                                                    {t("customerDetail.cancel")}
                                                </Button>
                                            </Space>
                                        ) : (
                                            <Space size={8}>
                                                {canSellInsurance && (
                                                    <Button
                                                        shape="round"
                                                        icon={<SafetyCertificateOutlined />}
                                                        disabled={initialLoad}
                                                        onClick={() =>
                                                            history.push(
                                                                `/sell-insurance?openModal=newPolicySale&customerId=${customerId}`,
                                                            )
                                                        }>
                                                        {tSell("buyInsuranceForThisCustomer")}
                                                    </Button>
                                                )}
                                                <Button
                                                    type="primary"
                                                    shape="round"
                                                    ghost
                                                    disabled={initialLoad}
                                                    onClick={() => setCustomerEdit(true)}>
                                                    {t("edit")}
                                                </Button>
                                            </Space>
                                        )}
                                    </div>
                                </div>

                                <CustomerAvatar
                                    imageUrl={customerinfo?.userImage}
                                    isEdit={customerEdit}
                                    loading={initialLoad}
                                    fileList={images}
                                    onFileChange={handleFileChange}
                                />

                                {!customerEdit ? (
                                    initialLoad ? (
                                        <div className="m-4">
                                            <Skeleton active paragraph={{ rows: 8 }} />
                                        </div>
                                    ) : (
                                        <CarDetailFieldTable
                                            title={t("personalInfo")}
                                            rows={detailRows}
                                            naFallback={na}
                                        />
                                    )
                                ) : (
                                    <CustomerDetailEditForm form={form} onFinish={handleFormSubmit} t={t} />
                                )}
                            </div>

                            <div className="flex min-w-0 flex-col gap-3">
                                {initialLoad ? (
                                    <div className="rounded-2xl border border-[#f0f0f0] bg-white p-8">
                                        <Skeleton active paragraph={{ rows: 6 }} />
                                    </div>
                                ) : (
                                    <CustomerLicenseSection
                                        customer={customerinfo}
                                        licenseVerificationStatus={licenseVerificationStatus}
                                        checkingVerification={checkingVerification}
                                        onRefreshVerification={() => handleLicenseVerification(customerinfo?.email)}
                                        t={t}
                                        na={na}
                                    />
                                )}

                                <CarDetailTabs tabs={CUSTOMER_DETAIL_TABS} active={active} onChange={setActive}>
                                    {renderActiveTab()}
                                </CarDetailTabs>
                            </div>
                        </div>
                    )}
                </div>
            </Spin>
        </div>
    );
}

Customerdetails.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            id: PropTypes.string,
        }),
    }),
};

export default Customerdetails;
